import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import Markdown from "../components/Markdown";
import InquiryForm from "../components/InquiryForm";
import { getReport, listReports } from "../lib/api";
import { REPORT } from "../constants/testIds";

const ReportDetail = () => {
  const { slug } = useParams();
  const [report, setReport] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getReport(slug)
      .then((r) => { setReport(r); setLoading(false); })
      .catch((e) => { setError(e.response?.status === 404 ? "not_found" : "error"); setLoading(false); });
  }, [slug]);

  useEffect(() => {
    if (report) {
      listReports({ category: report.category }).then((rs) => {
        setRelated(rs.filter((r) => r.slug !== report.slug).slice(0, 3));
      });
    }
  }, [report]);

  useEffect(() => {
    if (report?.content_status === "generating") {
      const t = setInterval(() => {
        getReport(slug).then((r) => {
          if (r.content_status !== "generating") {
            setReport(r);
            clearInterval(t);
          }
        });
      }, 4000);
      return () => clearInterval(t);
    }
  }, [report?.content_status, slug]);

  if (loading) {
    return (
      <Layout>
        <div data-theme="paper" style={{ minHeight: "calc(100vh - 88px)" }}>
          <div className="max-w-3xl mx-auto px-6 py-32 text-center">
            <Loader2 className="animate-spin mx-auto mb-4 text-[var(--blue-soft)]" />
            <div className="font-mono-label text-[var(--slate)]">Retrieving briefing…</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error === "not_found") {
    return (
      <Layout>
        <div data-theme="paper" style={{ minHeight: "calc(100vh - 88px)" }}>
          <div className="max-w-3xl mx-auto px-6 py-32 text-center">
            <h1 className="font-display text-4xl text-[var(--ivory)] mb-6">Report not found.</h1>
            <Link to="/library" className="editorial-link font-mono-label">Return to research →</Link>
          </div>
        </div>
      </Layout>
    );
  }

  if (!report) return null;

  const headings = (report.content || "")
    .split("\n")
    .filter((l) => l.trim().startsWith("## "))
    .map((l) => l.replace(/^##\s+/, ""));

  return (
    <Layout>
      <SEO
        title={`${report.title} | Steck Aviation Management`}
        description={report.subtitle || `Research from Steck Aviation Management on private aircraft ownership, management, and the business aviation market.`}
        image={report.cover_image_url || undefined}
        type="article"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": report.title,
          "description": report.subtitle || "",
          "publisher": { "@type": "Organization", "name": "Steck Aviation Management", "url": "https://www.steckaviationllc.com" },
          "keywords": (report.topics || []).join(", ")
        }}
      />
      <div data-theme="paper" style={{ minHeight: "calc(100vh - 88px)" }}>
        <article className="max-w-[1500px] mx-auto px-6 lg:px-12 py-12" data-testid={REPORT.container}>

          <Link
            to="/library"
            className="font-mono-label text-[var(--slate)] inline-flex items-center gap-2 hover:text-[#002868] transition-colors mb-10"
            data-testid={REPORT.backLink}
          >
            <ArrowLeft size={14} /> Research
          </Link>

          {/* HEADER */}
          <div className="max-w-4xl">
            <div className="font-mono-label text-[var(--blue-soft)] mb-5">{report.category}</div>
            <h1 className="font-display text-4xl md:text-6xl leading-[1.04] text-[var(--ivory)]">
              {report.title}
            </h1>
            {report.subtitle && (
              <p className="mt-7 font-serif-soft text-2xl md:text-3xl italic text-[var(--ivory-dim)] leading-snug">
                {report.subtitle}
              </p>
            )}
          </div>

          {/* COVER */}
          {report.cover_image_url && (
            <div className="my-14 image-bleed top-bottom h-[420px] md:h-[560px] overflow-hidden">
              <img
                src={report.cover_image_url}
                alt={report.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
            {/* MAIN BODY */}
            <div className="lg:col-span-8">
              {report.content_status === "generating" || !report.content ? (
                <div className="py-16 text-center border border-[#D4CFC5]">
                  <Loader2 className="animate-spin mx-auto mb-4 text-[#002868]" />
                  <div className="font-mono-label text-[var(--slate)]">
                    Research desk is composing this briefing…
                  </div>
                  <div className="text-[var(--slate-dim)] text-sm mt-2 font-light">
                    Long-form articles are generated on demand. Usually 30–60 seconds.
                  </div>
                </div>
              ) : (
                <div className="fade-in">
                  <Markdown source={report.content} />
                </div>
              )}
            </div>

            {/* SIDEBAR */}
            <aside className="lg:col-span-4 lg:sticky lg:top-32 self-start space-y-12">
              {headings.length > 0 && (
                <div data-testid={REPORT.toc}>
                  <div className="font-mono-label text-[var(--blue-soft)] mb-5 flex items-center gap-2">
                    <BookOpen size={12} /> Contents
                  </div>
                  <ul className="space-y-3 border-l-2 border-[#002868] pl-4">
                    {headings.map((h, i) => (
                      <li key={i} className="text-sm text-[var(--slate)] leading-snug font-light">
                        <span className="text-[#002868] font-mono mr-2 opacity-60">{String(i + 1).padStart(2, "0")}</span>
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {report.topics?.length > 0 && (
                <div>
                  <div className="font-mono-label text-[var(--blue-soft)] mb-5">Topics</div>
                  <ul className="space-y-2.5">
                    {report.topics.map((t) => (
                      <li key={t} className="text-sm text-[var(--ivory)] font-light">— {t}</li>
                    ))}
                  </ul>
                </div>
              )}

              {report.sources?.length > 0 && (
                <div data-testid={REPORT.sources}>
                  <div className="font-mono-label text-[var(--blue-soft)] mb-5">Sources</div>
                  <ul className="space-y-2.5">
                    {report.sources.map((s) => (
                      <li key={s} className="font-serif-soft text-base text-[var(--slate)] leading-snug italic">{s}</li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>

          {related.length > 0 && (
            <>
              <div className="divider-thin mt-28" />
              <div className="mt-16">
                <div className="font-mono-label text-[var(--blue-soft)] mb-7">Related research</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {related.map((r) => (
                    <Link
                      to={`/reports/${r.slug}`}
                      key={r.id}
                      className="group block border-t-2 border-[#D4CFC5] pt-6 hover:border-[#002868] transition-colors"
                    >
                      <div className="font-mono-label text-[var(--slate-dim)] mb-3">{r.category}</div>
                      <h3 className="font-display text-xl text-[var(--ivory)] group-hover:text-[#2251FF] transition-colors leading-tight">
                        {r.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="divider-thin mt-28" />
          <div className="mt-16 max-w-3xl mx-auto">
            <InquiryForm report={{ slug: report.slug, title: report.title }} />
          </div>
        </article>
      </div>
    </Layout>
  );
};

export default ReportDetail;
